// Types
import { Order } from "@/types/supabase";

// Libs
import { format, parseISO } from "date-fns";

// Icons
import {
  UserRound,
  MapPin,
  Clock,
  MessageSquareText,
  Store,
  Phone,
  TimerIcon,
} from "lucide-react";
import { useOrdersStore } from "./use-orders-store";
import { motion } from "framer-motion";

export default function OrderDetailInformation({ order }: { order: Order }) {
  const orderDuration = useOrdersStore((state) => state.orderDuration);
  const formatReceptionTime = (receptionAt: string | null) => {
    if (!receptionAt) return "—";
    try {
      const date = parseISO(receptionAt);
      return format(date, "h:mma");
    } catch (error) {
      return "—";
    }
  };

  const processedDuration = (duration: number | null) => {
    if (!duration) return "N/A";
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <article className="py-4 px-5 rounded-2xl space-y-4 border border-gray-200 flex-1">
      <header className="font-semibold text-black">Detalles del pedido</header>
      <section className="text-sm flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <div className="flex gap-1 text-gray-500 flex-col items-start xl:flex-row">
            <div className="flex items-center gap-2 ">
              <TimerIcon size={18} />
              <span className="font-semibold text-gray-600">Duración:</span>
            </div>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5, ease: "easeInOut" }}
            >
              {processedDuration(orderDuration) || "N/A"}
            </motion.span>
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="flex gap-1 text-gray-500 flex-col items-start xl:flex-row">
            <div className="flex items-center gap-2 ">
              <UserRound size={18} />
              <span className="font-semibold text-gray-600">Client:</span>
            </div>
            {order.customer?.full_name || "N/A"}
          </div>
        </div>
        <div className="flex justify-between items-start">
          <div className="flex gap-1 text-gray-500 flex-col items-start xl:flex-row">
            <div className="flex items-center gap-2 ">
              <Phone size={18} />
              <span className="font-semibold text-gray-600">Teléfono:</span>
            </div>
            {order.customer?.phone || "N/A"}
          </div>
        </div>
        <div className="flex gap-1 text-gray-500 flex-col items-start xl:flex-row">
          <div className="flex items-center gap-2">
            <MapPin size={18} />
            <span className="font-semibold text-gray-600 text-nowrap">
              Dirección entrega:
            </span>
          </div>
          {order.address?.full_address || "N/A"}
        </div>
        {order.reception_at && (
          <div className="flex gap-1 text-gray-500 flex-col items-start xl:flex-row">
            <div className="flex items-center gap-2">
              <Clock size={18} />
              <span className="font-semibold text-gray-600">
                Hora de entrega:
              </span>
            </div>
            {formatReceptionTime(order.reception_at)}
          </div>
        )}
        <div className="flex gap-1 text-gray-500 flex-col items-start xl:flex-row">
          <div className="flex items-center gap-2">
            <MessageSquareText size={18} />
            <span className="font-semibold text-gray-600">Comentarios:</span>
          </div>
          {order.comment
            ? order.comment.charAt(0).toUpperCase() + order.comment.slice(1)
            : "—"}
        </div>
        <div className="flex gap-1 text-gray-500 flex-col items-start xl:flex-row">
          <div className="flex items-center gap-2">
            <Store size={18} />
            <span className="font-semibold text-gray-600">Sucursal:</span>
          </div>
          {order.branch?.name || "—"}
        </div>
      </section>
    </article>
  );
}
